import ClientDisclaimer from '@/components/client-disclaimer';
import CohortMultiSelect, {
  getAllCohortSelection,
} from '@/components/cohorts/cohort-multi-select';
import SwipableTabs from '@/components/mobile/swipable-tabs';
import ThinkEducationSurveyModal from '@/components/think-education-survey-modal';
import { useMyCohorts } from '@/hooks/queries/cohorts';
import { useProfileDisplayFirstName } from '@/utils/profile-variables';
import { Selection } from '@nextui-org/react';
import { useEffect, useRef, useState } from 'react';
import { useUserStore } from '@/store/use-user-store';
import { useMediaQuery } from 'usehooks-ts';
import TabsSection from './tabs-section';
import TasksCarousel from './task-carousel';

const DashboardContent = () => {
  const hasAcceptedDisclaimer = useUserStore(
    (state) => state.hasAcceptedDisclaimer
  );
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { data: cohorts } = useMyCohorts();
  const { greetingFirstName, isGreetingReady } = useProfileDisplayFirstName();
  const [selectedCohortKeys, setSelectedCohortKeys] = useState<Selection>(
    new Set([])
  );
  const previousCohortIdsRef = useRef('');

  useEffect(() => {
    if (!cohorts?.results?.length) {
      setSelectedCohortKeys(new Set([]));
      previousCohortIdsRef.current = '';
      return;
    }

    const cohortIdsKey = cohorts.results
      .map((cohort) => cohort.id)
      .sort((a, b) => a - b)
      .join(',');

    if (previousCohortIdsRef.current !== cohortIdsKey) {
      previousCohortIdsRef.current = cohortIdsKey;
      setSelectedCohortKeys(getAllCohortSelection(cohorts.results));
    }
  }, [cohorts?.results]);

  const getTimeBasedGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const currentDay = daysOfWeek[new Date().getDay()];

  return (
    <>
      {!hasAcceptedDisclaimer && <ClientDisclaimer />}
      <ThinkEducationSurveyModal />

      <div className="mx-auto mb-28 p-2 sm:p-0">
        <div className="mb-3 sm:mb-8 mt-0 sm:mt-18">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-2xl font-bold text-gray-900">
              {getTimeBasedGreeting()}
              {isGreetingReady ? `, ${greetingFirstName}` : ''}!
            </h1>
            {cohorts?.results && cohorts.results.length > 1 && (
              <div className="w-full sm:max-w-xs shrink-0">
                <CohortMultiSelect
                  label="Filter by Cohorts"
                  selectedKeys={selectedCohortKeys}
                  onSelectionChange={setSelectedCohortKeys}
                  fetchMyCohorts
                />
              </div>
            )}
          </div>
          <p
            className={`text-gray-500 md:text-base mt-4 md:mt-2 ${isMobile ? 'text-md' : 'text-sm'}`}
          >
            {isMobile
              ? 'Ready to level up your leadership skills?'
              : `Happy ${currentDay}! You are one step closer to mastering your energies & actions`}
          </p>
        </div>

        {!isMobile && (
          <div className="flex flex-col sm:flex-row justify-between w-full gap-4">
            <div className="w-full">
              <TasksCarousel selectedCohortKeys={selectedCohortKeys} />
            </div>
          </div>
        )}

        {isMobile ? (
          <SwipableTabs selectedCohortKeys={selectedCohortKeys} />
        ) : (
          <TabsSection selectedCohortKeys={selectedCohortKeys} />
        )}
      </div>
    </>
  );
};

export default DashboardContent;
